import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { ArrowLeft, Calendar, MapPin, Users, Bike, Footprints } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/use-auth";
import { ShareEventButton } from "@/components/share-event-button";

export const Route = createFileRoute("/_authenticated/cafe/eventos/$eventId")({
  head: () => ({ meta: [{ title: "Detalle de visita — Brulo" }] }),
  component: CafeEventDetail,
});

type EventDetail = {
  id: string;
  name: string;
  event_date: string;
  event_time: string;
  distance_km: number | null;
  meeting_point: string;
  meeting_details: string | null;
  description: string | null;
  cafe_id: string | null;
  clubs: { name: string; city: string | null; type: string } | null;
  cafes: { name: string; location: string; owner_id: string } | null;
};

type Inscrito = {
  fecha_inscripcion: string;
  usuario_id: string;
  full_name: string | null;
};

function CafeEventDetail() {
  const { eventId } = Route.useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [denied, setDenied] = useState(false);
  const [event, setEvent] = useState<EventDetail | null>(null);
  const [inscritos, setInscritos] = useState<Inscrito[]>([]);

  useEffect(() => {
    if (!user) return;
    (async () => {
      const { data: myCafe } = await supabase
        .from("cafes")
        .select("id,name,location,owner_id")
        .eq("owner_id", user.id)
        .maybeSingle();

      if (!myCafe) {
        setDenied(true);
        setLoading(false);
        return;
      }

      const { data: ev } = await supabase
        .from("events")
        .select(
          "id,name,event_date,event_time,distance_km,meeting_point,meeting_details,description,cafe_id,clubs(name,city,type)",
        )
        .eq("id", eventId)
        .eq("cafe_id", myCafe.id)
        .maybeSingle();

      if (!ev) {
        setDenied(true);
        setLoading(false);
        return;
      }

      const event = {
        ...(ev as any),
        cafes: { name: myCafe.name, location: myCafe.location, owner_id: myCafe.owner_id },
      } as EventDetail;
      setEvent(event);

      const { data: ins } = await supabase
        .from("inscripciones")
        .select("usuario_id,fecha_inscripcion")
        .eq("evento_id", eventId)
        .order("fecha_inscripcion");
      const rows = (ins ?? []) as { usuario_id: string; fecha_inscripcion: string }[];

      let profiles: Record<string, string | null> = {};
      if (rows.length) {
        const { data: prof } = await supabase
          .from("profiles")
          .select("id,full_name")
          .in("id", rows.map((r) => r.usuario_id));
        (prof ?? []).forEach((p: { id: string; full_name: string | null }) => {
          profiles[p.id] = p.full_name;
        });
      }
      setInscritos(
        rows.map((r) => ({
          usuario_id: r.usuario_id,
          fecha_inscripcion: r.fecha_inscripcion,
          full_name: profiles[r.usuario_id] ?? null,
        })),
      );
      setLoading(false);
    })();
  }, [user, eventId]);

  if (loading) {
    return (
      <div className="mx-auto max-w-4xl px-4 md:px-6 py-20 text-muted-foreground">Cargando...</div>
    );
  }

  if (denied || !event) {
    return (
      <div className="mx-auto max-w-4xl px-4 md:px-6 py-20 space-y-4">
        <p className="text-muted-foreground">
          Esta visita no está disponible o no pertenece a tu cafetería.
        </p>
        <button
          onClick={() => navigate({ to: "/cafe/dashboard" })}
          className="text-primary hover:underline"
        >
          ← Mis visitas
        </button>
      </div>
    );
  }

  const type = event.clubs?.type ?? "run";
  const typeLabel = type === "bike" ? "Ciclismo" : type === "both" ? "Running y Ciclismo" : "Running";
  const TypeIcon = type === "bike" ? Bike : Footprints;

  return (
    <div className="mx-auto max-w-4xl px-4 md:px-6 py-12 space-y-10">
      <Link
        to="/cafe/dashboard"
        className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground transition"
      >
        <ArrowLeft className="w-4 h-4" />
        Mis visitas
      </Link>

      <div className="space-y-3">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 border border-primary/30 text-primary text-xs uppercase tracking-widest">
          <TypeIcon className="w-3.5 h-3.5" />
          {typeLabel}
        </div>
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <h1 className="font-display text-5xl">{event.name}</h1>
          <ShareEventButton
            variant="full"
            event={{
              id: event.id,
              name: event.name,
              event_date: event.event_date,
              event_time: event.event_time,
              meeting_point: event.meeting_point,
              cafe_name: event.cafes?.name ?? null,
              cafe_location: event.cafes?.location ?? null,
              distance_km: event.distance_km,
              club_name: event.clubs?.name ?? null,
            }}
          />
        </div>
        {event.clubs && (
          <p className="text-muted-foreground">
            Organiza <span className="text-foreground">{event.clubs.name}</span>
            {event.clubs.city ? ` · ${event.clubs.city}` : ""}
          </p>
        )}
      </div>

      <div className="bg-primary/10 border border-primary/40 rounded-lg p-8 text-center">
        <p className="font-display text-6xl text-primary">{inscritos.length}</p>
        <p className="text-sm uppercase tracking-widest text-muted-foreground mt-2">
          {inscritos.length === 1 ? "persona llegará a tu café" : "personas llegarán a tu café"}
        </p>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <InfoCard icon={<Calendar className="w-5 h-5 text-primary" />} title="Fecha y hora">
          <p>
            {new Date(event.event_date).toLocaleDateString("es-MX", {
              weekday: "long",
              day: "numeric",
              month: "long",
              year: "numeric",
            })}
          </p>
          <p className="text-muted-foreground">{event.event_time?.slice(0, 5)} hrs</p>
          {event.distance_km && (
            <p className="text-muted-foreground mt-1">{event.distance_km} km</p>
          )}
        </InfoCard>
        <InfoCard icon={<MapPin className="w-5 h-5 text-primary" />} title="Punto de reunión">
          <p>{event.cafes?.name}</p>
          <p className="text-muted-foreground">{event.cafes?.location}</p>
          {event.meeting_details && (
            <p className="text-sm text-muted-foreground mt-2 italic">
              {event.meeting_details}
            </p>
          )}
        </InfoCard>
      </div>

      {event.description && (
        <section>
          <h2 className="font-display text-2xl mb-3">Notas del organizador</h2>
          <div className="bg-card border border-border rounded-lg p-6 text-muted-foreground whitespace-pre-line">
            {event.description}
          </div>
        </section>
      )}

      <section>
        <div className="flex items-center gap-3 mb-5">
          <Users className="w-6 h-6 text-primary" />
          <h2 className="font-display text-3xl">Inscritos</h2>
        </div>
        {inscritos.length === 0 ? (
          <div className="bg-card border border-border rounded-lg p-8 text-center text-muted-foreground">
            Aún no hay personas inscritas.
          </div>
        ) : (
          <ul className="bg-card border border-border rounded-lg divide-y divide-border">
            {inscritos.map((i) => (
              <li
                key={i.usuario_id}
                className="flex items-center justify-between px-5 py-3"
              >
                <span>{i.full_name || "Deportista"}</span>
                <span className="text-xs text-muted-foreground">
                  {new Date(i.fecha_inscripcion).toLocaleDateString("es-MX", {
                    day: "numeric",
                    month: "short",
                  })}
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

function InfoCard({
  icon,
  title,
  children,
}: {
  icon: React.ReactNode;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center gap-2 mb-3">
        {icon}
        <h3 className="font-display text-lg">{title}</h3>
      </div>
      <div className="space-y-1">{children}</div>
    </div>
  );
}
